<template>
  <div
    class="min-h-screen bg-gradient-to-b from-gray-900 to-black text-white relative min-w-[390px]"
  >
    <!-- Desktop Layout (>1100px) -->
    <div class="">
      <!-- Header -->
      <div
        class="absolute top-0 left-0 right-0 z-10 flex items-center justify-between pb-2 mid:pt-20 mid:pl-10 mid:pr-10 md:pt-12 md:pl-7 md:pr-7 max-md:justify-center max-md:pt-12"
      >
        <!-- Logo -->
        <div class="flex items-center space-x-2">
          <Logo />
        </div>

        <!-- Login / Search Menu Button -->
        <div class="flex gap-2 max-md:hidden">
          <Button v-if="isLoggedIn" size="lg" variant="outline" @click="handleGoToSearchMenu">
            Search Menu
          </Button>
          <Button v-else size="lg" @click="handleLogin">
            {{ $t("signup.Login") }}
          </Button>
        </div>
      </div>

      <!-- Welcome State -->
      <div
        v-if="messages.length <= 1"
        class="flex flex-col h-screen overflow-y-auto scrollbar-hide pt-32"
      >
        <!-- Content Area -->
        <div
          class="flex flex-col px-6 overflow-y-auto scrollbar-hide pb-10"
          style="height: calc(100vh - 200px)"
        >
          <!-- Central Logo -->
          <div class="flex flex-col items-center">
            <div class="relative mb-4">
              <img
                src="@/assets/icons/AI/AIIcon.svg"
                alt="AI"
                class="w-[83px] h-[83px] ssm:w-[83px] ssm:h-[83px] md:w-[108px] md:h-[108px] mid:w-[120px] mid:h-[120px] mid:mt-20 animate-breathing"
              />
            </div>
            <!-- Main Title -->
            <div class="">
              <span class="text-[#666666] text-2xl px-3 py-1"> Genie </span>
            </div>
            <h1
              class="text-2xl ssm:text-[24px] md:text-[38px] font-normal text-center mb-5 md:mb-[35px] md:mt-[35px] ssm:mt-8 mt-8"
            >
              {{ $t("aiChat.perfectGpuMatch") }}
            </h1>

            <!-- Description -->
            <div
              class="max-w-[745px] text-left tracking-wide flex justify-center"
            >
              <p
                class="text-gray-300 text-base ssm:text-[16px] md:text-[22px] leading-[30px] mb-2"
              >
                {{ $t("aiChat.welcomeDescription.text") }}
                <span class="font-bold text-white">{{
                $t("aiChat.welcomeDescription.example1")
                }}</span>
                {{ $t("aiChat.welcomeDescription.middleText") }}
                <span class="font-bold text-white">{{
                  $t("aiChat.welcomeDescription.example2")
                }}</span>
                {{$t('aiChat.welcomeDescription.middleText1')}}
                 <span class="font-bold text-white">{{
                  $t("aiChat.welcomeDescription.recommend1")
                }}</span>
                {{$t('aiChat.welcomeDescription.middleText2')}}
                 <span class="font-bold text-white">{{
                  $t("aiChat.welcomeDescription.clusterBuilding")
                }}</span>
              </p>
            </div>
          </div>
        </div>

        <!-- Fixed Bottom Input Area -->
        <div class="fixed bottom-0 left-0 right-0 p-[40px] bg-[#2A2A2A]">
          <div class="w-full max-w-4xl mx-auto">
            <ChatInput
              @send="handleSendMessage"
              :disabled="isTyping || isLoading || isSubmitting"
              :allow-empty="allowEmptyInput"
            />
          </div>
        </div>
      </div>

      <!-- Chat State -->
      <div v-else class="flex flex-col max-w-6xl mx-auto px-6 pt-32">
        <!-- Messages Area -->
        <div
          ref="messagesContainer"
          class="overflow-y-auto pb-24 scrollbar-hide"
          style="height: calc(100vh - 200px)"
        >
          <div ref="messagesWrapper" class="space-y-6 py-6" data-messages-wrapper>
            <div
              v-for="(message, displayIndex) in displayMessages"
              :key="displayIndex"
              class="w-full"
            >
              <!-- User Message -->
              <div v-if="message.type === 'user'" class="flex justify-end mb-6">
                <div
                  class="max-w-[70%] bg-gray-700 text-white px-4 py-3 rounded-2xl"
                >
                  {{ message.content }}
                </div>
              </div>

              <!-- Assistant Message -->
              <div v-else class="flex justify-start mb-6">
                <div class="w-[95%]">
                  <!-- Cluster Building Confirmation Summary -->
                  <div v-if="message.formStep === 'confirm' && message.messageParams">
                    <!-- Error message (if any) - only show if there's an error -->
                    <div v-if="message.content && message.messageKey === 'clusterBuilding.submitError'" class="whitespace-pre-line mb-3 text-red-400">
                      {{ message.content }}
                    </div>
                    
                    <!-- Form summary -->
                    <ClusterConfirmSummary
                      :formData="message.messageParams"
                      :isSubmitting="isSubmitting"
                      :isActive="isConfirmFormActive(displayIndex + 1)"
                      @edit="handleEditClusterField"
                      @confirm="handleConfirmCluster"
                      @cancel="handleCancelCluster"
                    />
                  </div>

                  <!-- Regular text messages -->
                  <div v-else>
                    <div
                      class="text-white whitespace-pre-line text-lg leading-relaxed"
                    >
                      <template
                        v-if="
                          message.messageKey === 'aiChat.insufficientBudget' &&
                          message.messageParams
                        "
                      >
                        {{ $t("aiChat.insufficientBudget.text1") }}
                        <span class="font-bold text-white">{{
                          message.messageParams.current
                        }}</span>
                        {{ $t("aiChat.insufficientBudget.text2") }}
                        <span class="font-bold text-white">{{
                          message.messageParams.required
                        }}</span>
                        {{ $t("aiChat.insufficientBudget.text3") }}
                      </template>
                      <template
                        v-else-if="
                          message.messageKey === 'clusterBuilding.success' &&
                          message.messageParams
                        "
                      >
                        {{ $t("clusterBuilding.success", { email: message.messageParams.email }) }}
                      </template>
                      <template v-else>
                        {{ message.content }}
                      </template>
                    </div>

                    <!-- GPU Results (GpuTrainingResults 物件格式) -->
                    <!-- local 解法  -->
                    <div
                      v-if="message.gpuResults"
                      class="bg-gray-800 border border-gray-600 rounded-2xl p-6 mb-4"
                    >
                    <div class="font-medium mb-4 text-blue-400 text-lg">
                      {{ $t("gpu.trainingResults") }}
                    </div>
                    <div class="overflow-x-auto">
                      <div class="flex space-x-4 min-w-max pb-2">
                        <div
                          v-for="(tier, tierName) in message.gpuResults"
                          :key="tierName"
                          class="flex-shrink-0 w-64"
                        >
                          <div
                            class="bg-gray-700 border border-gray-600 rounded-lg p-4"
                          >
                            <div class="text-base font-bold text-blue-400 mb-4">
                              {{
                                $t(
                                  `gpu.${tierName
                                    .toLowerCase()
                                    .replace(" ", "")}`
                                )
                              }}
                            </div>
                            <div class="space-y-4">
                              <div class="bg-gray-800 rounded-lg p-4">
                                <div
                                  class="text-sm font-medium text-green-400 mb-2"
                                >
                                  💰 {{ $t("gpu.budgetFriendly") }}
                                </div>
                                <div class="text-sm text-white mb-1">
                                  {{ tier["Cheapest Flavor"].name }}
                                </div>
                                <div class="text-sm text-gray-400 mb-1">
                                  {{ tier["Cheapest Flavor"].gpu_type }} ×{{
                                    tier["Cheapest Flavor"].stock_quantity
                                  }}
                                </div>
                                <div
                                  class="text-sm text-yellow-400 font-bold mb-1"
                                >
                                  ${{
                                    tier[
                                      "Cheapest Flavor"
                                    ].total_cost.toLocaleString()
                                  }}
                                </div>
                                <div class="text-sm text-gray-400">
                                  {{
                                    tier["Cheapest Flavor"]
                                      .training_time_display
                                  }}
                                </div>
                              </div>
                              <div class="bg-gray-800 rounded-lg p-4">
                                <div
                                  class="text-sm font-medium text-blue-400 mb-2"
                                >
                                  ⚡ {{ $t("gpu.fastest") }}
                                </div>
                                <div class="text-sm text-white mb-1">
                                  {{ tier["Fastest Flavor"].name }}
                                </div>
                                <div class="text-sm text-gray-400 mb-1">
                                  {{ tier["Fastest Flavor"].gpu_type }} ×{{
                                    tier["Fastest Flavor"].stock_quantity
                                  }}
                                </div>
                                <div
                                  class="text-sm text-yellow-400 font-bold mb-1"
                                >
                                  ${{
                                    tier[
                                      "Fastest Flavor"
                                    ].total_cost.toLocaleString()
                                  }}
                                </div>
                                <div class="text-sm text-gray-400">
                                  {{
                                    tier["Fastest Flavor"].training_time_display
                                  }}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Note: gpuResults format doesn't include product_id, so no add to cart buttons -->
                  </div>

                  <!-- AI Results -->
                  <div
                    v-if="message.aiResults"
                    class="bg-gray-800 border border-gray-600 rounded-2xl p-6 mb-4 w-fit"
                  >
                    <div class="font-medium mb-4 text-blue-400 text-lg">
                      {{ $t("gpu.trainingResults") }}
                    </div>
                    <TooltipProvider :delay-duration="100">
                      <div
                        class="flex flex-col md:flex-row gap-4 md:overflow-x-auto"
                      >
                        <div
                          v-for="result in message.aiResults"
                          :key="result.name"
                          class="w-full md:min-w-[235px] md:w-auto"
                        >
                          <div
                            class="bg-gray-700 border border-gray-600 rounded-lg p-4 relative"
                          >
                            <!-- Information Icon in top right corner -->
                            <div class="absolute top-3 right-3">
                              <GpuInfoTooltip :name="result.name" />
                            </div>

                            <div v-if="!result.promote" class="text-base font-bold text-blue-400 mb-4">
                              <span v-if="result.calculate_result === 'first'"
                                >🚀 {{ $t("gpu.fastestTime") }}</span
                              >
                              <span
                                v-else-if="result.calculate_result === 'second'"
                                >🌟 {{ $t("gpu.bestValue") }}</span
                              >
                              <span
                                v-else-if="result.calculate_result === 'third'"
                                >⭐️ {{ $t("gpu.bestValue") }}</span
                              >
                              <span
                                v-else-if="
                                  result.calculate_result === 'cheapest'
                                "
                                >💰 {{ $t("gpu.budgetFriendly") }}</span
                              >
                            </div>
                            <div v-else class=" min-h-6">
                            </div>
                            <div class="space-y-4">
                              <div class="bg-gray-800 rounded-lg p-4">
                                <div
                                  :class="{
                                    'text-md font-medium mb-2': true,
                                    'text-green-400':
                                      result.calculate_result === 'cheapest',
                                    'text-blue-400':
                                      result.calculate_result !== 'cheapest',
                                  }"
                                ></div>
                                <div class="text-md text-white mb-1">
                                  {{ result.name }}
                                </div>
                                <div class="text-md text-gray-400 mb-1">
                                  {{ result.gpu_type }} <span v-if="!result.promote">x {{ result.stock_quantity }}</span><span v-if="!result.promote"> (Servers)</span>
                                </div>

                                <!-- Add to Cart Button for AI Results -->
                                <div class="mt-1">
                                  <div v-if="!result.promote"
                                    class="text-md text-yellow-400 font-bold mb-1"
                                  >
                                    ${{ result.total_cost.toLocaleString() }}
                                  </div>
                                  <div v-if="result.promote" class="text-md text-yellow-400 font-bold mb-1 min-h-5">
                                    ${{ result.total_cost.toLocaleString() }}<span v-if="result.billing_type === 'month'">/ month</span>
                                  </div>
                                  <div v-if="!result.promote && result.tokens_per_hour" class="text-md text-gray-400">
                                    {{ $t("gpu.tokensPerHour") }}: {{ formatLargeNumber(result.tokens_per_hour) }}
                                  </div>
                                  <div v-if="!result.promote && result.total_tokens" class="text-md text-gray-400 mb-1">
                                    {{ $t("gpu.totalTokens") }}: {{ formatLargeNumber(result.total_tokens) }}
                                  </div>
                                  <div v-if="!result.promote" class="text-md text-gray-400 mb-3">
                                    <template
                                      v-if="result.training_hours <= 24"
                                    >
                                      {{ result.training_hours.toFixed(0) }}
                                      {{ $t("gpu.hours") }}
                                    </template>
                                    <template v-else>
                                      {{
                                        (result.training_hours / 24).toFixed(2)
                                      }}
                                      {{ $t("gpu.days") }}
                                    </template>
                                  </div>

                                  <!-- Add to Cart Button for AI Results -->
                                  <div class="mt-3">
                                    <Button
                                      size="sm"
                                      @click="
                                        handleAddToCart(
                                          result.product_id.toString(),
                                          result.billing_type,
                                          result.promote ? 1 : result.stock_quantity,
                                          result.training_hours
                                        )
                                      "
                                      class="w-full"
                                    >
                                      {{ $t("cart.addToCart") }}
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </TooltipProvider>
                  </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Typing indicator -->
            <div v-if="isTyping" class="flex justify-start mb-6">
              <div class="flex items-center space-x-2 text-gray-400">
                <div class="flex space-x-1">
                  <div
                    class="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                  ></div>
                  <div
                    class="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                    style="animation-delay: 0.1s"
                  ></div>
                  <div
                    class="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                    style="animation-delay: 0.2s"
                  ></div>
                </div>
              </div>
            </div>

            <!-- Cancel cluster building button -->
            <div v-if="isClusterBuilding && !isTyping" class="flex justify-start mb-6">
              <Button
                variant="outline"
                size="sm"
                class="text-xs text-red-400 border-red-400/50 hover:bg-red-400/10 hover:text-red-300"
                @click="cancelClusterBuilding"
              >
                {{ $t('clusterBuilding.cancelButton') }}
              </Button>
            </div>
          </div>
        </div>

        <!-- Fixed Bottom Input for Chat State -->
        <div class="fixed bottom-0 left-0 right-0 p-[40px] bg-[#2A2A2A]">
          <div class="max-w-4xl mx-auto">
            <ChatInput
              @send="handleSendMessage"
              :disabled="isTyping || isLoading || isSubmitting"
              :allow-empty="allowEmptyInput"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Login Dialog -->
    <Dialog v-model:open="showLoginDialog">
      <DialogContent class="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{{ loginDialogTitle }}</DialogTitle>
          <DialogDescription>
            {{ loginDialogDescription }}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button @click="() => handleDialogConfirm(isLoggedIn)">{{
            $t("aiChat.dialog.confirm")
          }}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, nextTick, watch, computed } from "vue";
import { Button } from "@/components/ui/button";
import { createApiCalculationStrategy } from "~/composables/useGpuStrategies";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { TooltipProvider } from "@/components/ui/tooltip";
import ClusterConfirmSummary from '@/components/Chat/ClusterConfirmSummary.vue';

const config = useRuntimeConfig();
const token = useCookie("token");
const { t } = useI18n();
const { showConfirm } = useConfirmDialog();

// Set page meta
definePageMeta({
  layout: false,
  // ssr: true 是預設值，保持 SSR 開啟以獲得更好的 SEO 和首次載入效能
});

// Language management is handled by the global plugin

// Choose strategy - you can change this to test different approaches
const useApiStrategy = true; // Set to true to use AI API, false for local calculation
const strategy = useApiStrategy ? createApiCalculationStrategy() : undefined;

// 格式化大數字為 K/M/B/T
const formatLargeNumber = (num: number): string => {
  let value: number
  let unit: string
  if (num >= 1e12) { value = num / 1e12; unit = 'T' }
  else if (num >= 1e9) { value = num / 1e9; unit = 'B' }
  else if (num >= 1e6) { value = num / 1e6; unit = 'M' }
  else if (num >= 1e3) { value = num / 1e3; unit = 'K' }
  else return num.toString()
  const formatted = value % 1 === 0 ? value.toFixed(0) : value.toFixed(1)
  return `${formatted} ${unit}`
}

// Use existing chat functionality with strategy injection
const { messages, isTyping, isLoading, sendMessage, allowEmptyInput, cancelClusterBuilding, isClusterBuilding } = useAiChat(strategy);
const messagesContainer = ref<HTMLDivElement | null>(null);
const messagesWrapper = ref<HTMLDivElement | null>(null);

// Filter out welcome message for display
const displayMessages = computed(() => {
  return messages.value.filter((_: any, index: number) => index > 0); // Skip first welcome message
});

const scrollToBottom = () => {
  nextTick(() => {
    setTimeout(() => {
      const wrapper = messagesWrapper.value;
      
      if (wrapper && wrapper.children.length > 0) {
        const lastEl = wrapper.children[wrapper.children.length - 1] as HTMLElement;
        // Scroll to show the top of the last message
        lastEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 150);
  });
};

const handleSendMessage = async (messageContent: string) => {
  await sendMessage(messageContent);
  scrollToBottom();
};

const handleLogin = () => {
  navigateTo("/home");
};

// Cluster Building 相關邏輯
const isSubmitting = ref(false);
const latestConfirmMessageIndex = ref(-1);
const submittedConfirmIndexes = ref<Set<number>>(new Set());

watch(messages, (newMessages) => {
  // 找到最後一個 confirm 訊息的 index
  let lastConfirmIndex = -1;
  for (let i = newMessages.length - 1; i >= 0; i--) {
    if (newMessages[i].formStep === 'confirm') {
      lastConfirmIndex = i;
      break;
    }
  }
  
  latestConfirmMessageIndex.value = lastConfirmIndex;
}, { deep: true });

// 檢查某個 confirm form 是否應該是 active 的
const isConfirmFormActive = (index: number) => {
  return index === latestConfirmMessageIndex.value && !submittedConfirmIndexes.value.has(index);
};

const handleEditClusterField = async (fieldNumber: string) => {
  await sendMessage(fieldNumber);
};

const handleConfirmCluster = async () => {
  isSubmitting.value = true;
  // 標記當前 form 為已提交
  if (latestConfirmMessageIndex.value >= 0) {
    submittedConfirmIndexes.value.add(latestConfirmMessageIndex.value);
  }
  await sendMessage('confirm');
  // 1 秒後恢復 ChatInput
  setTimeout(() => {
    isSubmitting.value = false;
  }, 1000);
};

const handleCancelCluster = async () => {
  isSubmitting.value = true;
  // 標記當前 form 為已提交（取消也算）
  if (latestConfirmMessageIndex.value >= 0) {
    submittedConfirmIndexes.value.add(latestConfirmMessageIndex.value);
  }
  await sendMessage('cancel');
  setTimeout(() => {
    isSubmitting.value = false;
  }, 1000);
};

// 處理加入購物車
const { savePendingCartItem, getPendingCartItems, clearPendingCartItems } =
  usePendingCart();
const { calculateAdjustedAmount } = useCartAmount();

// Dialog 狀態
const showLoginDialog = ref(false);
const loginDialogTitle = ref("");
const loginDialogDescription = ref("");
const isLoggedIn = ref(false);

// 頁面載入時檢查登入狀態
onMounted(async () => {
  isLoggedIn.value = await checkLoginStatus();
});

// 檢查登入狀態
const checkLoginStatus = async () => {
  try {
    await $fetch("/api/auth/check-login", {
      method: "POST",
    });
    return true;
  } catch (error) {
    // 如果發生錯誤，視為未登入
    return false;
  }
};

// 前往 Search Menu
const handleGoToSearchMenu = () => {
  navigateTo("/home");
};

const handleAddToCart = async (
  productId: string,
  planType: string,
  amount: number = 1,
  trainingHours?: number
) => {
  // 計算調整後的數量
  const adjustedAmount = calculateAdjustedAmount(amount, trainingHours);

  // 如果數量被調整（減少），顯示提示 dialog
  if (adjustedAmount < amount) {
    const confirmed = await showConfirm({
      title: t("cart.quantityAdjustment.title"),
      description: t("cart.quantityAdjustment.description", {
        original: amount,
        adjusted: adjustedAmount,
      }),
      confirmText: t("cart.quantityAdjustment.confirm"),
      cancelText: t("cart.quantityAdjustment.cancel"),
    });

    if (!confirmed) {
      return; // 取消加入購物車
    }
  }

  isLoggedIn.value = await checkLoginStatus();
  console.log("amount : ", amount, "adjustedAmount : ", adjustedAmount);
  
  if (isLoggedIn.value) {
    // 已登入，顯示成功對話框
    loginDialogTitle.value = t("aiChat.dialog.success");
    loginDialogDescription.value = t("aiChat.dialog.addToCartSuccess");
    showLoginDialog.value = true;
  } else {
    // 未登入，顯示登入提示對話框
    loginDialogTitle.value = t("aiChat.dialog.loginRequired");
    loginDialogDescription.value = t("aiChat.dialog.loginRequiredDesc");
    showLoginDialog.value = true;
  }

  // 儲存到 cookie（無論是否登入都儲存，使用調整後的數量）
  savePendingCartItem(productId.toString(), planType, "chat", adjustedAmount);
};

const handleDialogConfirm = (isLogin: boolean) => {
  showLoginDialog.value = false;
  if (isLogin) {
    // 跳轉到首頁
    navigateTo("/home");
  } else {
    //前往登入頁面
    navigateTo(`${config.public.GSNurl}/auth/login/`, { external: true });
  }
};

// 測試函數
const testSavePendingCart = () => {
  console.log("=== 測試儲存 ===");
  savePendingCartItem("39", "hour", "chat");
  handleAddToCart("39", "hour");
  console.log('已儲存 productId: "39", planType: hourly');
};

const testGetPendingCart = () => {
  console.log("=== 測試取出 ===");
  const items = getPendingCartItems();
  console.log("取出的項目:", items);
  items.forEach((item) => {
    console.log(item.productId, item.planType, item.fromPage);
  });

  if (items.length === 0) {
    console.log("購物車是空的");
  } else {
    console.log(`共有 ${items.length} 個項目`);
  }
};

// Auto-scroll when new messages arrive
watch(
  [messages, isTyping],
  () => {
    scrollToBottom();
  },
  { deep: true }
);
</script>

<style scoped>
/* Hide scrollbar */
.scrollbar-hide {
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  scrollbar-width: none; /* Firefox */
}

.scrollbar-hide::-webkit-scrollbar {
  display: none; /* Safari and Chrome */
}

/* Breathing animation */
@keyframes breathing {
  0%,
  100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.85;
  }
}

.animate-breathing {
  animation: breathing 3s ease-in-out infinite;
}
</style>
